import { useInfiniteQuery } from '@tanstack/react-query';

import { parseDateRange } from '@/components/data-table/filters-v1/components/data-table-date-range-picker-v2-filter';
import { FilterValues } from '@/hooks/use-filter-values';
import { flattenInfinitePages, getNextPageParam } from '@/lib/constants/pagination';
import { ProjectBillingModel } from '@/modules/projects';
import { formatDateToISO, normalizeToStartOfDayUTC } from '@/utils/date-formatters';

import { getGenerateInvoiceData } from '../api';
import {
  INVOICES_FILTER_KEY_BILLING_MODEL,
  INVOICES_FILTER_KEY_CLIENT,
  INVOICES_FILTER_KEY_PROJECT,
} from '../constants/invoices-filter-keys';

export const GENERATE_INVOICE_DATA_QUERY_KEY = 'generateInvoiceData';

export const mapGenerateInvoiceDataFilters = (filters: FilterValues['values']) => {
  const apiFilters: {
    searchString?: string;
    startDate?: string;
    endDate?: string;
    clientIds?: number[];
    projectIds?: number[];
    billingModels?: ProjectBillingModel[];
  } = {};

  if (filters.searchString) {
    apiFilters.searchString = filters.searchString as string;
  }

  if (filters.billingPeriod && typeof filters.billingPeriod === 'string') {
    const dateRange = parseDateRange(filters.billingPeriod);

    if (dateRange?.startDate) {
      apiFilters.startDate = formatDateToISO(normalizeToStartOfDayUTC(dateRange.startDate));
    }

    if (dateRange?.endDate) {
      apiFilters.endDate = formatDateToISO(normalizeToStartOfDayUTC(dateRange.endDate));
    }
  }

  if (filters[INVOICES_FILTER_KEY_CLIENT]) {
    const arr = Array.isArray(filters[INVOICES_FILTER_KEY_CLIENT])
      ? filters[INVOICES_FILTER_KEY_CLIENT]
      : [filters[INVOICES_FILTER_KEY_CLIENT] as string];
    apiFilters.clientIds = arr.map(Number);
  }

  if (filters[INVOICES_FILTER_KEY_PROJECT]) {
    const arr = Array.isArray(filters[INVOICES_FILTER_KEY_PROJECT])
      ? filters[INVOICES_FILTER_KEY_PROJECT]
      : [filters[INVOICES_FILTER_KEY_PROJECT] as string];
    apiFilters.projectIds = arr.map(Number);
  }

  if (filters[INVOICES_FILTER_KEY_BILLING_MODEL]) {
    const arr = Array.isArray(filters[INVOICES_FILTER_KEY_BILLING_MODEL])
      ? filters[INVOICES_FILTER_KEY_BILLING_MODEL]
      : [filters[INVOICES_FILTER_KEY_BILLING_MODEL] as string];
    apiFilters.billingModels = arr as ProjectBillingModel[];
  }

  return apiFilters;
};

export const useGenerateInvoiceData = ({ filters }: { filters: FilterValues['values'] }) => {
  return useInfiniteQuery({
    queryKey: [GENERATE_INVOICE_DATA_QUERY_KEY, filters],
    queryFn: ({ pageParam }) => {
      const apiFilters = mapGenerateInvoiceDataFilters(filters);
      return getGenerateInvoiceData({ cursor: pageParam, filters: apiFilters });
    },
    getNextPageParam,
    initialPageParam: undefined as number | undefined,
    // Opts out of the app-wide focus/absence revalidation: this screen refreshes via
    // `usePollingStaleness` + `applySmartRefresh`, which merges the new response
    // against the user's edits. A silent refetch would skip that merge.
    refetchOnWindowFocus: false,
    select: flattenInfinitePages,
  });
};
